import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PublicLayout from "../shell/PublicLayout";
import Overline from "../components/Overline";
import Button from "../components/Button";
import { TextInput, Field } from "../components/Input";
import { SubmitContactUs } from "../../service/service";
import { paths } from "../routing/paths";

/*
 * /contact-us in the redesign — replaces the legacy ContactUs page (modal
 * popups, background-image info box). Same user/contact-us endpoint; the
 * name field still splits into f_name/l_name for the backend. One white
 * form card + a navy "other ways to reach us" card; inline success and
 * error states, mirroring AskQuestionPage's grammar.
 */

const textareaClasses =
  "font-sans text-[16px] leading-6 font-normal px-4 py-3 w-full box-border border rounded-xl min-h-[160px] resize-y " +
  "bg-white text-ic-navy placeholder:text-ic-placeholder transition-all duration-150 ease-out " +
  "focus:outline-none focus:border-ic-blue focus:shadow-ic-focus-soft disabled:bg-ic-disabledBg disabled:cursor-not-allowed";

const EMPTY = { fullName: "", email: "", comment: "" };

const mailIcon = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="3" />
    <path d="M3.5 7l7.2 5.4a2 2 0 0 0 2.6 0L20.5 7" />
  </svg>
);

const questionIcon = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M9.4 9.2a2.7 2.7 0 0 1 5.2 1c0 1.8-2.6 2.2-2.6 3.6" />
    <circle cx="12" cy="17" r="0.5" fill="currentColor" stroke="none" />
  </svg>
);

export default function ContactPage() {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState({ state: "idle", message: "" });
  const [touched, setTouched] = useState(false);

  const nameMissing = form.fullName.trim().length === 0;
  const emailMissing = form.email.trim().length === 0;
  const commentMissing = form.comment.trim().length === 0;
  const invalid = nameMissing || emailMissing || commentMissing;
  const busy = status.state === "sending";

  const update = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (invalid || busy) return;
    setStatus({ state: "sending", message: "" });
    // The endpoint takes a split name — first word is f_name, the rest l_name.
    const nameParts = form.fullName.trim().split(" ");
    try {
      const result = await SubmitContactUs({
        f_name: nameParts.shift(),
        l_name: nameParts.join(" "),
        email: form.email.trim(),
        comment: form.comment.trim(),
      });
      if (result?.status) {
        setStatus({ state: "sent", message: result.message || "" });
        setForm(EMPTY);
        setTouched(false);
      } else {
        setStatus({ state: "error", message: result?.message || "We couldn't send your message. Please try again." });
      }
    } catch (err) {
      setStatus({ state: "error", message: typeof err === "string" ? err : err?.message || "We couldn't send your message. Please try again." });
    }
  };

  return (
    <PublicLayout>
      <Helmet>
        <title>Contact us | Inside Chassidus</title>
      </Helmet>
      <main className="max-w-[1280px] w-full mx-auto px-5 pt-5 pb-10 flex flex-col gap-5">
        <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-[14px] leading-5 whitespace-nowrap">
          <Link to={paths.home()} className="text-ic-blue font-semibold py-3 px-1 -my-3 inline-flex items-center min-h-[44px] hover:underline">
            Home
          </Link>
          <span className="text-ic-slate">→</span>
          <span className="text-ic-body font-semibold px-1">Contact us</span>
        </nav>

        <section className="flex flex-col gap-2">
          <Overline>Get in touch</Overline>
          <h1 className="m-0 font-extrabold text-black tracking-[-0.5px]" style={{ fontSize: "clamp(26px, 6vw, 40px)", lineHeight: 1.2 }}>
            Contact us
          </h1>
          <p className="m-0 text-[18px] leading-7 text-ic-body max-w-[640px]">
            Send a message directly to the Inside Chassidus staff — questions about the site, feedback, or anything else.
          </p>
        </section>

        <section className="grid gap-5 items-start" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))" }}>
          <div className="flex flex-col gap-4 max-w-[720px] w-full">
            {status.state === "sent" ? (
              <div role="status" className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised p-6 flex flex-col gap-4">
                <div className="w-12 h-12 rounded-full bg-[rgba(36,154,26,0.1)] text-ic-success flex items-center justify-center">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M5 12.5l4.5 4.5L19 7.5" />
                  </svg>
                </div>
                <h2 className="m-0 text-[24px] font-bold leading-8 tracking-[-0.3px] text-black">Your message is in</h2>
                <p className="m-0 text-[18px] leading-7 text-ic-body">
                  {status.message || "Thank you — we'll get back to you as soon as we can."}
                </p>
                <div className="flex gap-3 flex-wrap">
                  <Button size="lg" onClick={() => setStatus({ state: "idle", message: "" })}>
                    Send another message
                  </Button>
                  <Link
                    to={paths.home()}
                    className="inline-flex items-center h-12 px-5 text-[16px] font-semibold text-ic-body border border-ic-borderGray rounded-3xl no-underline transition-all duration-150 ease-out hover:border-ic-blue hover:text-ic-blue hover:no-underline"
                  >
                    Back to the homepage
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} noValidate className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised p-6 flex flex-col gap-5">
                <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))" }}>
                  <Field
                    label="Full name"
                    htmlFor="contact-name"
                    required
                    error={touched && nameMissing ? "Please tell us your name." : undefined}
                  >
                    <TextInput
                      id="contact-name"
                      name="fullName"
                      type="text"
                      autoComplete="name"
                      required
                      aria-required="true"
                      aria-invalid={touched && nameMissing}
                      error={touched && nameMissing}
                      value={form.fullName}
                      onChange={update}
                      disabled={busy}
                    />
                  </Field>
                  <Field
                    label="Email"
                    htmlFor="contact-email"
                    required
                    error={touched && emailMissing ? "Please add an email so we can reply." : undefined}
                  >
                    <TextInput
                      id="contact-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      inputMode="email"
                      required
                      aria-required="true"
                      aria-invalid={touched && emailMissing}
                      error={touched && emailMissing}
                      value={form.email}
                      onChange={update}
                      disabled={busy}
                    />
                  </Field>
                </div>
                <Field
                  label="Your message"
                  htmlFor="contact-message"
                  required
                  error={touched && commentMissing ? "Please write your message before sending." : undefined}
                >
                  <textarea
                    id="contact-message"
                    name="comment"
                    required
                    aria-required="true"
                    aria-invalid={touched && commentMissing}
                    placeholder="What would you like to tell us?"
                    value={form.comment}
                    onChange={update}
                    disabled={busy}
                    className={`${textareaClasses} ${touched && commentMissing ? "border-ic-error shadow-ic-error-ring" : "border-ic-lightGray"}`}
                  />
                </Field>
                {status.state === "error" && (
                  <p role="alert" className="m-0 text-[14px] leading-5 text-ic-error">
                    {status.message}
                  </p>
                )}
                <div className="flex items-center gap-4 flex-wrap">
                  <Button type="submit" size="lg" disabled={busy}>
                    {busy ? "Sending…" : "Send message"}
                  </Button>
                  <span className="text-[14px] leading-5 text-ic-slate">We never share your email.</span>
                </div>
              </form>
            )}
          </div>

          {/* Other ways in — navy card, list-category grammar */}
          <aside
            className="bg-ic-navy border border-white/10 rounded-xl shadow-ic-raised-dark flex flex-col gap-6 max-w-[480px] w-full"
            style={{ padding: "clamp(20px, 5vw, 32px)" }}
          >
            <h2 className="m-0 text-[20px] font-bold leading-7 tracking-[-0.2px] text-white">Other ways to reach us</h2>
            <div className="flex items-start gap-4">
              <span className="flex-shrink-0 w-11 h-11 rounded-xl bg-ic-navyCard text-ic-sky inline-flex items-center justify-center">{mailIcon}</span>
              <div className="flex flex-col gap-1 min-w-0">
                <span className="text-[14px] leading-5 text-ic-lightGray">Prefer email?</span>
                <a
                  href="mailto:Info@insidechassidus.org"
                  className="text-[16px] font-semibold leading-6 text-white underline inline-flex items-center min-h-[44px] ic-wrap-anywhere hover:text-ic-sky"
                >
                  Info@insidechassidus.org
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="flex-shrink-0 w-11 h-11 rounded-xl bg-ic-navyCard text-ic-sky inline-flex items-center justify-center">{questionIcon}</span>
              <div className="flex flex-col gap-1 min-w-0">
                <span className="text-[14px] leading-5 text-ic-lightGray">A question in Torah or Chassidus?</span>
                <Link
                  to={paths.askQuestion()}
                  className="text-[16px] font-semibold leading-6 text-white underline inline-flex items-center min-h-[44px] hover:text-ic-sky hover:underline"
                >
                  Ask Rabbi Paltiel directly →
                </Link>
              </div>
            </div>
          </aside>
        </section>
      </main>
    </PublicLayout>
  );
}
